import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Input,
  Label,
} from '@pantry/ui';
import { useCreateShoppingList } from '@pantry/api-client';
import { AnalyticsEvents } from '@pantry/analytics';
import { useAnalytics } from '@/hooks/useAnalytics';
import { toast } from 'sonner';

interface CreateListDialogProps {
  householdId: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: (listId: string) => void;
}

/**
 * Dialog to create a new shopping list.
 */
export function CreateListDialog({ householdId, open, onClose, onSuccess }: CreateListDialogProps) {
  const { t } = useLingui();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  const createList = useCreateShoppingList();
  const { track } = useAnalytics();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError(null);

    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(t`Please enter a list name.`);
      return;
    }

    try {
      const created = await createList.mutateAsync({
        householdId,
        data: { name: trimmed },
      });
      track(AnalyticsEvents.SHOPPING_LIST_CREATED, { householdId });
      setName('');
      onSuccess?.(created.id);
      onClose();
    } catch {
      toast.error(t`Failed to create list. Please try again.`);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setName('');
      setNameError(null);
      createList.reset();
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Trans context="dialog_title">Create Shopping List</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>Give your new list a name to get started.</Trans>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="list-name">
              <Trans>List name</Trans>
            </Label>
            <Input
              id="list-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t`e.g. Weekly Groceries`}
              autoFocus
              maxLength={100}
            />
            {nameError && <p className="text-sm text-destructive">{nameError}</p>}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              <Trans context="action_button">Cancel</Trans>
            </Button>
            <Button type="submit" disabled={createList.isPending}>
              {createList.isPending ? (
                <Trans>Creating...</Trans>
              ) : (
                <Trans context="action_button">Create</Trans>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
